import React, { useEffect, useRef, useState } from "react";
import { API } from "@/lib/api";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Bot, Send, Loader2, RotateCcw, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { PlanGate } from "@/components/PlanGate";

const QUICK = ["Quais as possíveis causas?", "Qual sequência de testes devo fazer?", "Que perguntas fazer ao cliente?", "Escreva a descrição técnica para o laudo"];

function AssistantInner() {
  const [sid, setSid] = useState(() => localStorage.getItem("nh-ai-sid") || `s${Date.now()}`);
  const [ctx, setCtx] = useState({ equipment: "", brand: "", model: "" });
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { localStorage.setItem("nh-ai-sid", sid); api.get(`/ai/assistant/${sid}`).then(({ data }) => setMsgs(data)).catch(() => {}); }, [sid]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async (t) => {
    const message = (t ?? text).trim();
    if (!message || busy) return;
    setText(""); setBusy(true);
    setMsgs((m) => [...m, { role: "user", content: message }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/ai/assistant`, { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` }, credentials: "include", body: JSON.stringify({ session_id: sid, message, ...ctx }) });
      if (!res.ok) throw new Error("fail");
      const reader = res.body.getReader(); const dec = new TextDecoder(); let buf = "";
      while (true) {
        const { value, done } = await reader.read(); if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n"); buf = parts.pop();
        for (const p of parts) {
          const line = p.replace(/^data: /, "").trim(); if (!line || line === "[DONE]") continue;
          try { const { t: tok } = JSON.parse(line); setMsgs((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], content: c[c.length - 1].content + tok }; return c; }); } catch (e) { console.error(e); }
        }
      }
    } catch (e) { toast.error("A IA não respondeu. Tente novamente."); setMsgs((m) => m.slice(0, -1)); } finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-3 pb-32 md:pb-4" data-testid="assistant-page">
      <div className="flex items-center justify-between">
        <div><h1 className="font-display font-extrabold text-2xl flex items-center gap-2"><Bot className="h-6 w-6 text-primary" /> Assistente IA</h1><p className="text-xs text-muted-foreground">Possíveis causas, testes e textos técnicos — sempre como sugestão para você confirmar.</p></div>
        <Button variant="ghost" size="sm" onClick={() => { setSid(`s${Date.now()}`); setMsgs([]); }} data-testid="assistant-new"><RotateCcw className="h-4 w-4 mr-1" /> Nova</Button>
      </div>
      <Card className="p-3 grid grid-cols-3 gap-2" data-testid="assistant-context">
        <Input value={ctx.equipment} onChange={(e) => setCtx({ ...ctx, equipment: e.target.value })} placeholder="Equipamento" className="h-10" data-testid="assistant-equipment" />
        <Input value={ctx.brand} onChange={(e) => setCtx({ ...ctx, brand: e.target.value })} placeholder="Marca" className="h-10" data-testid="assistant-brand" />
        <Input value={ctx.model} onChange={(e) => setCtx({ ...ctx, model: e.target.value })} placeholder="Modelo" className="h-10" data-testid="assistant-model" />
      </Card>
      <div className="space-y-3 min-h-[200px]" data-testid="assistant-messages">
        {msgs.length === 0 && (
          <Card className="p-4 text-sm text-muted-foreground"><p className="mb-2 flex items-center gap-1"><Sparkles className="h-4 w-4 text-primary" /> Descreva o sintoma e os testes que já fez. Exemplos rápidos:</p>
            <div className="flex flex-wrap gap-2">{QUICK.map((q) => <button key={q} onClick={() => send(q)} className="h-9 px-3 rounded-full border border-border text-xs hover:border-primary" data-testid="assistant-quick">{q}</button>)}</div></Card>
        )}
        {msgs.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[88%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-primary text-primary-foreground rounded-br-md" : "bg-card border border-border rounded-bl-md"}`} data-testid={`assistant-msg-${m.role}`}>
              {m.content || (busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "")}
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <div className="fixed bottom-20 left-0 right-0 md:static z-40 bg-background/95 backdrop-blur border-t md:border-0 border-border p-3 md:p-0">
        <div className="max-w-2xl mx-auto flex gap-2 items-end">
          <Textarea value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} placeholder="Ex.: Ar-condicionado 12k não gela, compressor liga, ventilação ok..." className="min-h-[48px] max-h-32 text-base" rows={1} data-testid="assistant-input" />
          <Button onClick={() => send()} disabled={busy || !text.trim()} className="h-12 w-12 shrink-0" data-testid="assistant-send">{busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}</Button>
        </div>
      </div>
    </div>
  );
}

export default function Assistant() {
  return <PlanGate feature="ai" title="Assistente IA" description="Tire dúvidas técnicas, gere descrições profissionais e sequências de testes com inteligência artificial."><AssistantInner /></PlanGate>;
}
